import {
  DocsCallout,
  DocsContent,
  DocsLayout,
  DocsScreenshot,
  DocsStepList,
  H2,
  H3,
} from '@/components/docs'

export default function DocsCaseManagement() {
  return (
    <DocsLayout>
      <DocsContent
        title="Case Management"
        description="Learn how to create, organize, and manage investigation cases in XENDREL. Cases are the foundation of your work, containing all documents, entities, and analysis for an investigation."
        previousPage={{ title: 'Quick Start', href: '/docs/getting-started' }}
        nextPage={{ title: 'Document Processing', href: '/docs/document-processing' }}
      >
        <H2 id="creating-a-case">Creating a Case</H2>

        <p className="text-foreground mb-6 leading-relaxed">
          Cases in XENDREL act as containers for your investigations. Each case has its own document
          collection, knowledge graph, and extracted entities, keeping your work organized and
          separate.
        </p>

        <DocsStepList
          steps={[
            {
              title: 'Open the New Case Dialog',
              description: (
                <>
                  From the Cases dashboard, click the{' '}
                  <strong className="text-foreground">New Case</strong> button in the top right
                  corner.
                </>
              ),
            },
            {
              title: 'Enter Basic Information',
              description: (
                <>
                  Provide a case name that clearly identifies the investigation. Good naming
                  conventions include dates, subject matter, or case numbers (e.g., "2024-001
                  Financial Fraud Investigation").
                </>
              ),
            },
            {
              title: 'Add Description (Optional)',
              description: (
                <>
                  Use the description field to document the investigation scope, key objectives, and
                  any important notes. This helps team members understand the case context.
                </>
              ),
            },
            {
              title: 'Create and Configure',
              description: (
                <>
                  Click <strong className="text-foreground">Create</strong> to finalize. You'll be
                  redirected to the case detail view where you can start adding documents.
                </>
              ),
            },
          ]}
        />

        <DocsScreenshot
          src="/docs/images/cases/case-creation.svg"
          alt="New Case Dialog"
          caption="Creating a new case with name and description"
        />

        <H2 id="case-statuses">Case Statuses</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Cases progress through different statuses that reflect their lifecycle:
        </p>

        <div className="space-y-4 my-6">
          <StatusCard
            status="Draft"
            color="gray"
            description="Initial state for new cases. Use this while setting up the case and adding initial documents."
          />
          <StatusCard
            status="Active"
            color="green"
            description="The case is under active investigation. This is the primary working state where most analysis happens."
          />
          <StatusCard
            status="Closed"
            color="blue"
            description="Investigation is complete. The case remains accessible for reference but is no longer being actively worked."
          />
          <StatusCard
            status="Archived"
            color="amber"
            description="Long-term storage for cases that may need future reference. Can be unarchived if needed."
          />
        </div>

        <DocsCallout variant="info">
          Changing a case status doesn't affect its data or accessibility. It's primarily for
          organizational purposes and workflow tracking.
        </DocsCallout>

        <H2 id="managing-case-documents">Managing Case Documents</H2>

        <H3 id="viewing-documents">Viewing Documents</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Open the Documents panel from the case toolbar to see all documents in your case. Each
          document shows:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">File name</strong> - Original document name
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Type</strong> - PDF, DOCX, CSV, etc.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Processing status</strong> - Pending, processing,
              completed, or failed
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Entity count</strong> - Number of entities
              extracted
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Upload date</strong> - When the document was added
            </span>
          </li>
        </ul>

        <H3 id="document-actions">Document Actions</H3>
        <p className="text-foreground mb-4 leading-relaxed">For each document, you can:</p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">View</strong> - Open the original document
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Reprocess</strong> - Run extraction again (useful
              after schema updates)
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Delete</strong> - Remove the document and its
              extracted entities
            </span>
          </li>
        </ul>

        <DocsCallout variant="warning" title="Deleting Documents">
          Deleting a document also removes all entities that were extracted solely from that
          document. Entities that appear in multiple documents will remain in the graph.
        </DocsCallout>

        <H2 id="case-settings">Case Settings</H2>

        <p className="text-foreground mb-4 leading-relaxed">
          Access case settings through the Settings dropdown (gear icon) in the case toolbar:
        </p>

        <H3 id="reinitialize-indexes">Reinitialize Indexes</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          If you experience slow queries or search issues, reinitializing the database indexes can
          help. This rebuilds the search and query optimization structures without affecting your
          data.
        </p>

        <H2 id="case-list-and-navigation">Case List and Navigation</H2>

        <DocsScreenshot
          src="/docs/images/cases/case-list.svg"
          alt="Case List View"
          caption="The Cases dashboard showing all your investigations"
        />

        <H3 id="filtering-cases">Filtering Cases</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Use the filter options to find cases quickly:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Status filter</strong> - Show only active, closed,
              or archived cases
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Search</strong> - Find cases by name or
              description
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>
              <strong className="text-foreground">Sort</strong> - Order by creation date, last
              modified, or name
            </span>
          </li>
        </ul>

        <H3 id="case-cards">Case Cards</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          Each case card displays key metrics at a glance:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Number of documents</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Total entities extracted</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Current status</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Last activity timestamp</span>
          </li>
        </ul>

        <H2 id="deleting-and-archiving">Deleting and Archiving Cases</H2>

        <H3 id="archiving">Archiving a Case</H3>
        <p className="text-foreground mb-4 leading-relaxed">
          To archive a case, change its status to "Archived" from the case settings. Archived cases:
        </p>

        <ul className="space-y-2 my-4 text-foreground">
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Remain fully accessible and searchable</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Are hidden from the default case list view</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-600 dark:text-amber-500 mt-1">•</span>
            <span>Can be unarchived at any time</span>
          </li>
        </ul>

        <H3 id="deleting">Deleting a Case</H3>
        <DocsCallout variant="warning" title="Permanent Action">
          Deleting a case permanently removes all documents, extracted entities, and the knowledge
          graph. This action cannot be undone. Consider archiving instead if you may need the data
          later.
        </DocsCallout>

        <p className="text-foreground mb-4 leading-relaxed">
          To delete a case, open the case settings menu and select "Delete Case". You'll be asked to
          confirm the deletion by typing the case name.
        </p>
      </DocsContent>
    </DocsLayout>
  )
}

function StatusCard({
  status,
  color,
  description,
}: {
  status: string
  color: 'gray' | 'green' | 'blue' | 'amber'
  description: string
}) {
  const colorClasses = {
    gray: 'border-gray-500/30 bg-gray-500/10',
    green: 'border-green-500/30 bg-green-500/10',
    blue: 'border-blue-500/30 bg-blue-500/10',
    amber: 'border-amber-500/30 bg-amber-500/10',
  }

  const badgeClasses = {
    gray: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    green: 'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
    blue: 'bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30',
    amber: 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30',
  }

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3 mb-2">
        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${badgeClasses[color]}`}>
          {status}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  )
}
